import { createClient, SupabaseClient } from '@supabase/supabase-js'

// Supabase client instance
let supabaseInstance: SupabaseClient | null = null

// CREDENCIALES HARDCODEADAS PARA MAGIC PATTERNS DEPLOYMENT
const SUPABASE_URL = 'https://sanpzousriwcnatnenpp.supabase.co'
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNhbnB6b3Vzcml3Y25hdG5lbnBwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njg5MTk3NzksImV4cCI6MjA4NDQ5NTc3OX0.G6lHZGDu5QziIWl12jXx8DvrCb2ogGF0eA2F7XzOUL4'

// Get Supabase URL (try env first, fallback to hardcoded)
const getSupabaseUrl = () => {
  if (
    typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_SUPABASE_URL
  ) {
    return import.meta.env.VITE_SUPABASE_URL
  }
  return SUPABASE_URL
}

// Get Supabase anon key (try env first, fallback to hardcoded)
const getSupabaseAnonKey = () => {
  if (
    typeof import.meta !== 'undefined' &&
    import.meta.env?.VITE_SUPABASE_ANON_KEY
  ) {
    return import.meta.env.VITE_SUPABASE_ANON_KEY
  }
  return SUPABASE_ANON_KEY
}

// Initialize Supabase client
const initializeSupabase = () => {
  const url = getSupabaseUrl()
  const key = getSupabaseAnonKey()

  if (url && key) {
    console.log('🟢 Initializing Supabase client...')
    supabaseInstance = createClient(url, key)
    return supabaseInstance
  }

  console.error('❌ Supabase not configured')
  supabaseInstance = null
  return null
}

// Get current Supabase client (reinitialize if needed)
export const getSupabase = () => {
  if (!supabaseInstance) {
    initializeSupabase()
  }
  return supabaseInstance
}

// Legacy export for compatibility
export const supabase = getSupabase()

// Check if Supabase is configured
export const isSupabaseConfigured = () => {
  const url = getSupabaseUrl()
  const key = getSupabaseAnonKey()
  return Boolean(url && key)
}

// Database helpers
export const supabaseClient = {
  // ============================================
  // ORDERS
  // ============================================
  async getOrders() {
    const client = getSupabase()
    if (!client) {
      throw new Error('Supabase not configured')
    }

    console.log('📥 Fetching orders from Supabase...')
    const { data, error } = await client
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('❌ Error fetching orders:', error)
      throw error
    }

    console.log('✅ Fetched', data?.length || 0, 'orders from Supabase')
    return data || []
  },

  async createOrder(order: any) {
    const client = getSupabase()
    if (!client) {
      throw new Error('Supabase not configured')
    }

    console.log('📤 Creating order in Supabase:', order)
    const { data, error } = await client.from('orders').insert([order]).select()

    if (error) {
      console.error('❌ Error creating order:', error)
      throw error
    }

    console.log('✅ Order created successfully:', data[0])
    return data[0]
  },

  async updateOrder(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('📝 Updating order', id, 'with:', updates)
    const { data, error } = await client
      .from('orders')
      .update(updates)
      .eq('id', id)
      .select()

    if (error) {
      console.error('❌ Error updating order:', error)
      throw error
    }

    console.log('✅ Order updated successfully')
    return data[0]
  },

  async deleteOrder(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('orders').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // EMPLOYEES
  // ============================================
  async getEmployees() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employees')
      .select('*')
      .order('name', { ascending: true })

    if (error) throw error
    return data || []
  },

  async createEmployee(employee: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employees')
      .insert([employee])
      .select()

    if (error) throw error
    return data[0]
  },

  async updateEmployee(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('employees')
      .update(updates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteEmployee(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('employees').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // COMPANIES
  // ============================================
  async getCompanies() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('📥 Fetching companies from Supabase...')
    const { data, error } = await client
      .from('companies')
      .select('*')
      .order('name', { ascending: true })

    if (error) {
      console.error('❌ Error fetching companies:', error)
      throw error
    }

    // Transform snake_case to camelCase for frontend compatibility
    const companies = (data || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      rnc: c.rnc,
      logo: c.logo,
      branches: c.branches || [],
      employees: c.employees || 0,
      taxExempt: c.tax_exempt,
      subsidy: c.subsidy,
      orderCutoffTime: c.order_cutoff_time,
      deliveryStartTime: c.delivery_start_time,
      deliveryEndTime: c.delivery_end_time,
      contactName: c.contact_name,
      contactEmail: c.contact_email,
      contactPhone: c.contact_phone,
      createdAt: c.created_at,
    }))

    console.log('✅ Fetched', companies.length, 'companies from Supabase')
    return companies
  },

  async createCompany(company: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    // Transform camelCase to snake_case for Supabase
    const dbCompany = {
      name: company.name,
      rnc: company.rnc,
      logo: company.logo || null,
      branches: company.branches || [],
      employees: company.employees || 0,
      tax_exempt: company.taxExempt || false,
      subsidy: company.subsidy || 'RD$ 0',
      order_cutoff_time: company.orderCutoffTime || '23:00',
      delivery_start_time: company.deliveryStartTime || '12:00',
      delivery_end_time: company.deliveryEndTime || '14:00',
      contact_name: company.contactName || null,
      contact_email: company.contactEmail || null,
      contact_phone: company.contactPhone || null,
    }

    console.log('📤 Creating company in Supabase:', dbCompany)
    const { data, error } = await client
      .from('companies')
      .insert([dbCompany])
      .select()

    if (error) {
      console.error('❌ Error creating company:', error)
      throw error
    }

    console.log('✅ Company created successfully')
    return data[0]
  },

  async updateCompany(id: number, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    // Transform camelCase to snake_case for Supabase
    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.rnc !== undefined) dbUpdates.rnc = updates.rnc
    if (updates.logo !== undefined) dbUpdates.logo = updates.logo
    if (updates.branches !== undefined) dbUpdates.branches = updates.branches
    if (updates.taxExempt !== undefined)
      dbUpdates.tax_exempt = updates.taxExempt
    if (updates.subsidy !== undefined) dbUpdates.subsidy = updates.subsidy
    if (updates.orderCutoffTime !== undefined)
      dbUpdates.order_cutoff_time = updates.orderCutoffTime
    if (updates.deliveryStartTime !== undefined)
      dbUpdates.delivery_start_time = updates.deliveryStartTime
    if (updates.deliveryEndTime !== undefined)
      dbUpdates.delivery_end_time = updates.deliveryEndTime
    if (updates.contactName !== undefined)
      dbUpdates.contact_name = updates.contactName
    if (updates.contactEmail !== undefined)
      dbUpdates.contact_email = updates.contactEmail
    if (updates.contactPhone !== undefined)
      dbUpdates.contact_phone = updates.contactPhone

    console.log('📝 Updating company', id, 'with:', dbUpdates)
    const { data, error } = await client
      .from('companies')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      console.error('❌ Error updating company:', error)
      throw error
    }

    console.log('✅ Company updated successfully')
    return data[0]
  },

  async deleteCompany(id: number) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('companies').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // MENU ITEMS
  // ============================================
  async getMenuItems() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('📥 Fetching menu items from Supabase...')
    const { data, error } = await client
      .from('menu_items')
      .select('*')
      .order('name', { ascending: true })

    if (error) {
      console.error('❌ Error fetching menu items:', error)
      throw error
    }

    // Transform snake_case to camelCase
    const items = (data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      description: item.description,
      category: item.category,
      price: parseFloat(item.price),
      image: item.image,
      catalogType: item.catalog_type,
      available: item.available,
      createdAt: item.created_at,
    }))

    console.log('✅ Fetched', items.length, 'menu items from Supabase')
    return items
  },

  async createMenuItem(item: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbItem = {
      id: item.id || `menu-${Date.now()}`,
      name: item.name,
      description: item.description,
      category: item.category,
      price: item.price,
      image: item.image || null,
      catalog_type: item.catalogType || 'Almuerzo',
      available: item.available !== false,
    }

    console.log('📤 Creating menu item in Supabase:', dbItem)
    const { data, error } = await client
      .from('menu_items')
      .insert([dbItem])
      .select()

    if (error) {
      console.error('❌ Error creating menu item:', error)
      throw error
    }

    console.log('✅ Menu item created successfully')
    return data[0]
  },

  async updateMenuItem(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.category !== undefined) dbUpdates.category = updates.category
    if (updates.price !== undefined) dbUpdates.price = updates.price
    if (updates.image !== undefined) dbUpdates.image = updates.image
    if (updates.catalogType !== undefined)
      dbUpdates.catalog_type = updates.catalogType
    if (updates.available !== undefined) dbUpdates.available = updates.available

    console.log('📝 Updating menu item', id, 'with:', dbUpdates)
    const { data, error } = await client
      .from('menu_items')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      console.error('❌ Error updating menu item:', error)
      throw error
    }

    console.log('✅ Menu item updated successfully')
    return data[0]
  },

  async deleteMenuItem(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('menu_items').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // MENU CATEGORIES
  // ============================================
  async getMenuCategories() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_categories')
      .select('*')
      .order('name', { ascending: true })

    if (error) {
      console.error('❌ Error fetching menu categories:', error)
      throw error
    }

    return (data || []).map((c: any) => c.name)
  },

  async createMenuCategory(name: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('menu_categories')
      .insert([{ name }])
      .select()

    if (error) {
      console.error('❌ Error creating menu category:', error)
      throw error
    }

    return data[0]
  },

  async deleteMenuCategory(name: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_categories')
      .delete()
      .eq('name', name)

    if (error) throw error
  },

  // ============================================
  // MENU ITEM SECTIONS
  // ============================================
  async getMenuItemSections(menuItemId: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('📥 Fetching sections for menu item:', menuItemId)
    const { data, error } = await client
      .from('menu_item_sections')
      .select('*, menu_section_options(*)')
      .eq('menu_item_id', menuItemId)
      .order('display_order', { ascending: true })

    if (error) {
      console.error('❌ Error fetching menu sections:', error)
      throw error
    }

    // Transform to frontend format
    const sections = (data || []).map((section: any) => ({
      id: section.id,
      menuItemId: section.menu_item_id,
      name: section.name,
      description: section.description,
      required: section.required,
      minSelections: section.min_selections,
      maxSelections: section.max_selections,
      displayOrder: section.display_order,
      options: (section.menu_section_options || [])
        .map((opt: any) => ({
          id: opt.id,
          sectionId: opt.section_id,
          name: opt.name,
          priceModifier: parseFloat(opt.price_modifier || 0),
          available: opt.available,
          displayOrder: opt.display_order,
        }))
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder),
    }))

    console.log('✅ Fetched', sections.length, 'sections')
    return sections
  },

  async createMenuSection(section: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbSection = {
      menu_item_id: section.menuItemId,
      name: section.name,
      description: section.description || null,
      required: section.required || false,
      min_selections: section.minSelections || 0,
      max_selections: section.maxSelections || 1,
      display_order: section.displayOrder || 0,
    }

    console.log('📤 Creating menu section:', dbSection)
    const { data, error } = await client
      .from('menu_item_sections')
      .insert([dbSection])
      .select()

    if (error) {
      console.error('❌ Error creating menu section:', error)
      throw error
    }

    console.log('✅ Menu section created successfully')
    return data[0]
  },

  async updateMenuSection(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.required !== undefined) dbUpdates.required = updates.required
    if (updates.minSelections !== undefined)
      dbUpdates.min_selections = updates.minSelections
    if (updates.maxSelections !== undefined)
      dbUpdates.max_selections = updates.maxSelections
    if (updates.displayOrder !== undefined)
      dbUpdates.display_order = updates.displayOrder

    const { data, error } = await client
      .from('menu_item_sections')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteMenuSection(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_item_sections')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ============================================
  // MENU SECTION OPTIONS
  // ============================================
  async createSectionOption(option: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbOption = {
      section_id: option.sectionId,
      name: option.name,
      price_modifier: option.priceModifier || 0,
      available: option.available !== false,
      display_order: option.displayOrder || 0,
    }

    console.log('📤 Creating section option:', dbOption)
    const { data, error } = await client
      .from('menu_section_options')
      .insert([dbOption])
      .select()

    if (error) {
      console.error('❌ Error creating section option:', error)
      throw error
    }

    console.log('✅ Section option created successfully')
    return data[0]
  },

  async updateSectionOption(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.priceModifier !== undefined)
      dbUpdates.price_modifier = updates.priceModifier
    if (updates.available !== undefined) dbUpdates.available = updates.available
    if (updates.displayOrder !== undefined)
      dbUpdates.display_order = updates.displayOrder

    const { data, error } = await client
      .from('menu_section_options')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) throw error
    return data[0]
  },

  async deleteSectionOption(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client
      .from('menu_section_options')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ============================================
  // PROMOTIONS
  // ============================================
  async getPromotions() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('📥 Fetching promotions from Supabase...')
    const { data, error } = await client
      .from('promotions')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('❌ Error fetching promotions:', error)
      throw error
    }

    // Transform snake_case to camelCase
    const promotions = (data || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      type: p.type,
      value: parseFloat(p.value),
      code: p.code,
      minOrderAmount: parseFloat(p.min_order_amount || 0),
      maxDiscount: parseFloat(p.max_discount || 0),
      validFrom: p.valid_from,
      validTo: p.valid_to,
      active: p.active,
      usageLimit: p.usage_limit || 0,
      usageCount: p.usage_count || 0,
      applicableCompanies: p.applicable_companies || [],
      applicableMenuItems: p.applicable_menu_items || [],
      createdBy: p.created_by,
      createdAt: p.created_at,
    }))

    console.log('✅ Fetched', promotions.length, 'promotions from Supabase')
    return promotions
  },

  async createPromotion(promotion: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbPromotion = {
      id: promotion.id || `promo-${Date.now()}`,
      name: promotion.name,
      description: promotion.description,
      type: promotion.type,
      value: promotion.value,
      code: promotion.code || null,
      min_order_amount: promotion.minOrderAmount || 0,
      max_discount: promotion.maxDiscount || 0,
      valid_from: promotion.validFrom,
      valid_to: promotion.validTo,
      active: promotion.active !== false,
      usage_limit: promotion.usageLimit || 0,
      usage_count: promotion.usageCount || 0,
      applicable_companies: promotion.applicableCompanies || [],
      applicable_menu_items: promotion.applicableMenuItems || [],
      created_by: promotion.createdBy || 'Admin',
    }

    console.log('📤 Creating promotion in Supabase:', dbPromotion)
    const { data, error } = await client
      .from('promotions')
      .insert([dbPromotion])
      .select()

    if (error) {
      console.error('❌ Error creating promotion:', error)
      throw error
    }

    console.log('✅ Promotion created successfully')
    return data[0]
  },

  async updatePromotion(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.name !== undefined) dbUpdates.name = updates.name
    if (updates.description !== undefined)
      dbUpdates.description = updates.description
    if (updates.type !== undefined) dbUpdates.type = updates.type
    if (updates.value !== undefined) dbUpdates.value = updates.value
    if (updates.code !== undefined) dbUpdates.code = updates.code
    if (updates.minOrderAmount !== undefined)
      dbUpdates.min_order_amount = updates.minOrderAmount
    if (updates.maxDiscount !== undefined)
      dbUpdates.max_discount = updates.maxDiscount
    if (updates.validFrom !== undefined)
      dbUpdates.valid_from = updates.validFrom
    if (updates.validTo !== undefined) dbUpdates.valid_to = updates.validTo
    if (updates.active !== undefined) dbUpdates.active = updates.active
    if (updates.usageLimit !== undefined)
      dbUpdates.usage_limit = updates.usageLimit
    if (updates.usageCount !== undefined)
      dbUpdates.usage_count = updates.usageCount

    console.log('📝 Updating promotion', id, 'with:', dbUpdates)
    const { data, error } = await client
      .from('promotions')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      console.error('❌ Error updating promotion:', error)
      throw error
    }

    console.log('✅ Promotion updated successfully')
    return data[0]
  },

  async deletePromotion(id: string) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { error } = await client.from('promotions').delete().eq('id', id)
    if (error) throw error
  },

  // ============================================
  // CLAIMS
  // ============================================
  async getClaims() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('📥 Fetching claims from Supabase...')
    const { data, error } = await client
      .from('claims')
      .select('*')
      .order('created_at', { ascending: false })

    if (error) {
      console.error('❌ Error fetching claims:', error)
      throw error
    }

    // Transform snake_case to camelCase
    const claims = (data || []).map((c: any) => ({
      id: c.id,
      orderId: c.order_id,
      orderNo: c.order_no,
      customerName: c.customer_name,
      company: c.company,
      branch: c.branch,
      type: c.type,
      status: c.status,
      priority: c.priority,
      description: c.description,
      resolution: c.resolution,
      createdAt: c.created_at,
      resolvedAt: c.resolved_at,
      createdBy: c.created_by,
      createdById: c.created_by_id,
    }))

    console.log('✅ Fetched', claims.length, 'claims from Supabase')
    return claims
  },

  async createClaim(claim: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbClaim = {
      id: claim.id || `claim-${Date.now()}`,
      order_id: claim.orderId,
      order_no: claim.orderNo,
      customer_name: claim.customerName,
      company: claim.company,
      branch: claim.branch,
      type: claim.type,
      status: claim.status || 'Pendiente',
      priority: claim.priority || 'Media',
      description: claim.description,
      resolution: claim.resolution || null,
      created_by: claim.createdBy,
      created_by_id: claim.createdById,
    }

    console.log('📤 Creating claim in Supabase:', dbClaim)
    const { data, error } = await client
      .from('claims')
      .insert([dbClaim])
      .select()

    if (error) {
      console.error('❌ Error creating claim:', error)
      throw error
    }

    console.log('✅ Claim created successfully')
    return data[0]
  },

  async updateClaim(id: string, updates: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbUpdates: any = {}
    if (updates.status !== undefined) dbUpdates.status = updates.status
    if (updates.resolution !== undefined)
      dbUpdates.resolution = updates.resolution
    if (updates.resolvedAt !== undefined)
      dbUpdates.resolved_at = updates.resolvedAt

    console.log('📝 Updating claim', id, 'with:', dbUpdates)
    const { data, error } = await client
      .from('claims')
      .update(dbUpdates)
      .eq('id', id)
      .select()

    if (error) {
      console.error('❌ Error updating claim:', error)
      throw error
    }

    console.log('✅ Claim updated successfully')
    return data[0]
  },

  // ============================================
  // ACTIVITY LOGS
  // ============================================
  async getActivityLogs() {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const { data, error } = await client
      .from('activity_logs')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(100)

    if (error) {
      console.error('❌ Error fetching activity logs:', error)
      throw error
    }

    // Transform to frontend format
    const logs = (data || []).map((log: any) => ({
      id: log.id?.toString() || `log-${Date.now()}`,
      timestamp: log.timestamp,
      user: 'Sistema',
      action: log.action,
      details: log.details,
      type: log.category,
    }))

    return logs
  },

  async createActivityLog(log: any) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    const dbLog = {
      action: log.action,
      details: log.details,
      category: log.type || log.category,
    }

    const { data, error } = await client
      .from('activity_logs')
      .insert([dbLog])
      .select()

    if (error) {
      console.error('❌ Error creating activity log:', error)
      throw error
    }

    return data[0]
  },

  // ============================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================
  subscribeToOrders(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    console.log('🔴 Setting up real-time subscription for orders...')

    return client
      .channel('orders-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        (payload) => {
          console.log('🔴 Real-time event received:', payload)
          callback(payload)
        },
      )
      .subscribe((status) => {
        console.log('🔴 Subscription status:', status)
      })
  },

  subscribeToEmployees(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('employees-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'employees' },
        callback,
      )
      .subscribe()
  },

  subscribeToClaims(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('claims-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'claims' },
        callback,
      )
      .subscribe()
  },

  subscribeToCompanies(callback: (payload: any) => void) {
    const client = getSupabase()
    if (!client) throw new Error('Supabase not configured')

    return client
      .channel('companies-channel')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'companies' },
        callback,
      )
      .subscribe()
  },
}

// Export types
export type Order = {
  id: string
  order_no: string
  order_date: string
  delivery_date: string
  type: string
  employee_name: string
  employee_id: string
  company: string
  branch: string
  product: string
  quantity: number
  catalog_type: string
  subtotal: string
  discount: string
  subsidy_amount: string
  subsidy_applied_by: string
  total: string
  status: 'Pendiente' | 'En Preparación' | 'Listo' | 'Entregado' | 'Cancelado'
  label_status: 'Pendiente' | 'Generado'
  tax_exempt: boolean
  created_at: string
}

export type Employee = {
  id: string
  name: string
  email: string
  username: string
  password: string
  position: string
  company_id: number
  branch_id: string
  status: string
  permissions: any
  created_at: string
}

export type Claim = {
  id: string
  order_id: string
  order_no: string
  customer_name: string
  company: string
  branch: string
  type: string
  status: string
  priority: string
  description: string
  resolution: string | null
  created_at: string
  resolved_at: string | null
  created_by: string
  created_by_id: string
}

export type ActivityLog = {
  id: number
  action: string
  details: string
  category: string
  timestamp: string
}

export type Company = {
  id: number
  name: string
  rnc: string
  logo: string | null
  branches: any[]
  employees: number
  tax_exempt: boolean
  subsidy: string
  order_cutoff_time: string
  delivery_start_time: string
  delivery_end_time: string
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  created_at: string
}

export type MenuItem = {
  id: string
  name: string
  description: string
  category: string
  price: number
  image: string | null
  catalog_type: string
  available: boolean
  created_at: string
}

export type Promotion = {
  id: string
  name: string
  description: string
  type: string
  value: number
  code: string | null
  min_order_amount: number
  max_discount: number
  valid_from: string
  valid_to: string
  active: boolean
  usage_limit: number
  usage_count: number
  applicable_companies: any[]
  applicable_menu_items: any[]
  created_by: string
  created_at: string
}
